import NiceModal from '@ebay/nice-modal-react'
import ChangeCircleOutlinedIcon from '@mui/icons-material/ChangeCircleOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import PersonIcon from '@mui/icons-material/Person'
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined'
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined'
import SwitchAccountOutlinedIcon from '@mui/icons-material/SwitchAccountOutlined'
import {
  Avatar,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Select,
  SortDirection,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material'
import {
  CompanyUsersRelation,
  CompanyUsersStatus,
  CompanyUserWithUserAndRole
} from '@shared/types/companies.types'
import { Permission } from '@shared/types/roles.types'
import { MouseEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import ContainedAvatar from '../../components/ContainedAvatar'
import EmptySearchResults from '../../components/EmptySearchResults'
import SearchInput from '../../components/forms/SearchInput'
import TableLoader from '../../components/loaders/TableLoader'
import { ChangeJobTitleModal } from '../../components/modals/ChangeJobTitleModal'
import { ChangeRoleModal } from '../../components/modals/ChangeRoleModal'
import { ChangeUserRelationModal } from '../../components/modals/ChangeUserRelationModal'
import { ExpelMemberFromCompanyModal } from '../../components/modals/ExpelMemberFromCompanyModal'
import { MemberProfileModal } from '../../components/modals/MemberProfileModal'
import RoleChip from '../../components/RoleChip'
import { useAuthStore } from '../../hooks/useAuthStore'
import {
  useCompanyRoles,
  useCompanyUser,
  useCompanyUsers
} from '../../hooks/useQueries/useCompanies'
import {
  getAvatarName,
  hasMoreFullAccessUsers,
  isDefaultRole
} from '../../utils/user.utils'

type MembersSortColumn = 'name' | 'email' | 'relation' | 'role' | ''

export default function CompaniesMembers() {
  const { t } = useTranslation()
  const params = useParams()
  const companyId = params.companyId!
  const { data: members, isLoading: isLoadingMembers } = useCompanyUsers(
    companyId,
    {
      status: CompanyUsersStatus.ACTIVE
    }
  )
  const { data: roles, isLoading: isLoadingRoles } = useCompanyRoles(companyId)
  const [searchText, setSearchText] = useState('')
  const [relation, setRelation] = useState('')
  const [role, setRole] = useState('')
  const [filteredMembers, setFilteredMembers] = useState<
    CompanyUserWithUserAndRole[]
  >([])
  const hasAppliedFilters = searchText || relation || role
  const [lastAppliedFilters, setLastAppliedFilters] = useState({
    searchText: '',
    relation: '',
    role: ''
  })
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedMember, setSelectedMember] =
    useState<CompanyUserWithUserAndRole | null>(null)
  const menuOpen = Boolean(anchorEl)
  const { userId } = useAuthStore()
  const { data: user } = useCompanyUser(companyId, userId!)
  const [orderBy, setOrderBy] = useState<MembersSortColumn>('')
  const [orderDirection, setOrderDirection] = useState<SortDirection>('asc')

  const { hasUpdatePermission, hasDeletePermission, hasCreatePermission } =
    useMemo(() => {
      const fullAccess = user?.role?.fullAccess ?? false
      const permissions = user?.permissions ?? []

      return {
        hasUpdatePermission:
          fullAccess || permissions.includes(Permission.USERS_UPDATE),
        hasDeletePermission:
          fullAccess || permissions.includes(Permission.USERS_DELETE),
        hasCreatePermission:
          fullAccess || permissions.includes(Permission.USERS_CREATE)
      }
    }, [user])

  useEffect(() => {
    setFilteredMembers(members || [])
  }, [members])

  const handleMenuClick = (
    event: MouseEvent<HTMLElement>,
    member: CompanyUserWithUserAndRole
  ) => {
    setAnchorEl(event.currentTarget)
    setSelectedMember(member)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
    setSelectedMember(null)
  }

  const handleFilter = (
    searchValue: string,
    relationValue: string,
    roleValue: string
  ) => {
    const result = members?.filter(member => {
      const matchSearch = JSON.stringify(member)
        .toLowerCase()
        .includes(searchValue.toLowerCase())

      const matchRelation = relationValue
        ? member.relation === relationValue
        : true
      const matchRole = roleValue ? member.role.name === roleValue : true

      return matchSearch && matchRelation && matchRole
    })

    setFilteredMembers(result || [])
    setLastAppliedFilters({
      searchText: searchValue,
      relation: relationValue,
      role: roleValue
    })
  }

  const areFiltersEqual = (
    current: typeof lastAppliedFilters,
    last: typeof lastAppliedFilters
  ) => {
    return (
      current.searchText === last.searchText &&
      current.relation === last.relation &&
      current.role === last.role
    )
  }

  const handleSort = (column: MembersSortColumn) => {
    let newOrderDirection: SortDirection = 'asc'

    if (orderBy === column) {
      newOrderDirection = orderDirection === 'asc' ? 'desc' : 'asc'
    }

    setOrderBy(column)
    setOrderDirection(newOrderDirection)

    const sorted = [...filteredMembers].sort((a, b) => {
      const getValue = (member: CompanyUserWithUserAndRole) => {
        switch (column) {
          case 'name':
            return `${member.user.name} ${member.user.lastName}`.toLowerCase()
          case 'email':
            return member.user.email.toLowerCase()
          case 'relation':
            return member?.relation?.toLowerCase() ?? ''
          case 'role':
            return member.role.name.toLowerCase()
          default:
            return ''
        }
      }

      const valA = getValue(a)
      const valB = getValue(b)

      return newOrderDirection === 'asc'
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA)
    })

    setFilteredMembers(sorted)
  }

  if (isLoadingMembers || isLoadingRoles) {
    return <TableLoader totalColumns={6} totalRows={3} />
  }

  return (
    <>
      {members?.length && (
        <TableContainer>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            paddingY='1rem'
          >
            <Stack
              direction='row'
              spacing='1rem'
              alignItems='center'
              paddingBottom='1rem'
            >
              <SearchInput
                searchText={searchText}
                setSearchText={setSearchText}
                handleFilter={handleFilter}
                additionalParams={[relation, role]}
              />
              <Select
                value={relation}
                onChange={e => setRelation(e.target.value)}
                sx={{
                  width: '13.75rem',
                  '& .MuiSelect-select': {
                    padding: '.75rem .75rem'
                  }
                }}
                displayEmpty
                renderValue={selected => {
                  if (selected === '') {
                    return (
                      <Typography color='text.secondary'>
                        {t('common.relation')}
                      </Typography>
                    )
                  }
                  return t(`relation.${selected.toLowerCase()}`)
                }}
              >
                <MenuItem value={CompanyUsersRelation.CLIENT}>
                  {t('relation.client')}
                </MenuItem>
                <MenuItem value={CompanyUsersRelation.EMPLOYEE}>
                  {t('relation.employee')}
                </MenuItem>
                <MenuItem value={CompanyUsersRelation.PROVIDER}>
                  {t('relation.provider')}
                </MenuItem>
              </Select>
              <Select
                value={role}
                onChange={e => setRole(e.target.value)}
                sx={{
                  width: '13.75rem',
                  '& .MuiSelect-select': {
                    padding: '.75rem .75rem'
                  }
                }}
                displayEmpty
                renderValue={selected => {
                  if (selected === '') {
                    return (
                      <Typography color='text.secondary'>
                        {t('common.role')}
                      </Typography>
                    )
                  }
                  return isDefaultRole(selected)
                    ? t(`role.${selected}`)
                    : selected
                }}
              >
                {roles?.map(role => (
                  <MenuItem key={role.id} value={role.name}>
                    {isDefaultRole(role.name)
                      ? t(`role.${role.name}`)
                      : role.name}
                  </MenuItem>
                ))}
              </Select>

              <Button
                variant='text'
                color='primary'
                onClick={() => handleFilter(searchText, relation, role)}
                disabled={
                  (!searchText && !relation && !role) ||
                  areFiltersEqual(
                    { searchText, relation, role },
                    lastAppliedFilters
                  )
                }
                sx={{ textTransform: 'none' }}
              >
                {t('common.apply_filters')}
              </Button>
              {hasAppliedFilters && (
                <Button
                  onClick={() => {
                    setSearchText('')
                    setRelation('')
                    setRole('')
                    setFilteredMembers(members)
                    setLastAppliedFilters({
                      searchText: '',
                      relation: '',
                      role: ''
                    })
                  }}
                  sx={{
                    textTransform: 'none',
                    color: 'secondary.main'
                  }}
                >
                  {t('common.delete_filters')}
                </Button>
              )}
            </Stack>
            {hasCreatePermission && (
              <Button
                variant='text'
                color='primary'
                startIcon={<PersonAddAltOutlinedIcon />}
                component={Link}
                to={`/companies/${companyId}/invitations/create`}
                sx={{
                  textTransform: 'none',
                  fontSize: '1rem',
                  padding: '0.5rem 0.6875rem'
                }}
              >
                {t('members_view.invite_button')}
              </Button>
            )}
          </Stack>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sortDirection={orderBy === 'name' ? orderDirection : false}
                >
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={
                      orderBy === 'name' && orderDirection !== false
                        ? (orderDirection as 'asc' | 'desc')
                        : undefined
                    }
                    hideSortIcon={false}
                    onClick={() => handleSort('name')}
                  >
                    {t('members_view.table_headers.user')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === 'email' ? orderDirection : false}
                >
                  <TableSortLabel
                    active={orderBy === 'email'}
                    direction={
                      orderBy === 'email' && orderDirection !== false
                        ? (orderDirection as 'asc' | 'desc')
                        : undefined
                    }
                    hideSortIcon={false}
                    onClick={() => handleSort('email')}
                  >
                    {t('members_view.table_headers.email')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderBy === 'relation' ? orderDirection : false
                  }
                >
                  <TableSortLabel
                    active={orderBy === 'relation'}
                    direction={
                      orderBy === 'relation' && orderDirection !== false
                        ? (orderDirection as 'asc' | 'desc')
                        : undefined
                    }
                    hideSortIcon={false}
                    onClick={() => handleSort('relation')}
                  >
                    {t('members_view.table_headers.relation')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === 'role' ? orderDirection : false}
                >
                  <TableSortLabel
                    active={orderBy === 'role'}
                    direction={
                      orderBy === 'role' && orderDirection !== false
                        ? (orderDirection as 'asc' | 'desc')
                        : undefined
                    }
                    hideSortIcon={false}
                    onClick={() => handleSort('role')}
                  >
                    {t('members_view.table_headers.role')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  {t('members_view.table_headers.job_title')}
                </TableCell>
                <TableCell align='right'>
                  {t('members_view.table_headers.actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMembers.length ? (
                filteredMembers.map((member, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Stack direction='row' spacing={1} alignItems='center'>
                        {member.user.avatarUri ? (
                          <ContainedAvatar
                            src={member.user.avatarUri}
                            alt={member.user.name}
                            sx={{ width: 32, height: 32 }}
                          />
                        ) : (
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {getAvatarName(
                              member.user.name || '',
                              member.user.lastName || ''
                            )}
                          </Avatar>
                        )}
                        <Typography variant='body2'>
                          {member.user.name} {member.user.lastName}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>
                        {member.user.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {member.relation === CompanyUsersRelation.CLIENT && (
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <HandshakeOutlinedIcon
                            fontSize='small'
                            color='action'
                          />
                          <Typography variant='body2'>
                            {t('relation.client')}
                          </Typography>
                        </Stack>
                      )}
                      {member.relation === CompanyUsersRelation.PROVIDER && (
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <LocalShippingOutlinedIcon
                            fontSize='small'
                            color='action'
                          />
                          <Typography variant='body2'>
                            {t('relation.provider')}
                          </Typography>
                        </Stack>
                      )}
                      {member.relation === CompanyUsersRelation.EMPLOYEE && (
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <PersonIcon fontSize='small' color='action' />
                          <Typography variant='body2'>
                            {t('relation.employee')}
                          </Typography>
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      <RoleChip role={member.role} />
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>{member.jobTitle}</Typography>
                    </TableCell>
                    <TableCell align='right'>
                      <IconButton
                        onClick={e => handleMenuClick(e, member)}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.12)'
                        }}
                      >
                        <MoreHorizIcon color='action' />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <EmptySearchResults />
                  </TableCell>
                </TableRow>
              )}
              <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleCloseMenu}
              >
                <MenuList>
                  <MenuItem
                    onClick={() => {
                      if (selectedMember) {
                        NiceModal.show(MemberProfileModal, {
                          member: selectedMember,
                          hasUpdatePermission
                        })
                        handleCloseMenu()
                      }
                    }}
                  >
                    <ListItemIcon>
                      <RemoveRedEyeOutlinedIcon fontSize='small' />
                    </ListItemIcon>
                    <ListItemText>{t('members_view.menu.view')}</ListItemText>
                  </MenuItem>
                  {hasUpdatePermission && (
                    <MenuItem
                      onClick={() => {
                        if (selectedMember) {
                          NiceModal.show(ChangeRoleModal, {
                            companyId,
                            userId: selectedMember.user.id,
                            currentRole: selectedMember.role.name,
                            workspaceModal: false,
                            hasMoreFullAccessUsers: hasMoreFullAccessUsers(
                              members,
                              selectedMember.user.id
                            )
                          })
                          handleCloseMenu()
                        }
                      }}
                    >
                      <ListItemIcon>
                        <SwitchAccountOutlinedIcon fontSize='small' />
                      </ListItemIcon>
                      <ListItemText>
                        {t('members_view.menu.change_role')}
                      </ListItemText>
                    </MenuItem>
                  )}
                  {hasUpdatePermission && (
                    <MenuItem
                      onClick={() => {
                        if (selectedMember) {
                          NiceModal.show(ChangeUserRelationModal, {
                            companyId,
                            userId: selectedMember.user.id,
                            currentRelation: selectedMember.relation,
                            workspaceModal: false
                          })
                          handleCloseMenu()
                        }
                      }}
                    >
                      <ListItemIcon>
                        <ChangeCircleOutlinedIcon fontSize='small' />
                      </ListItemIcon>
                      <ListItemText>
                        {t('members_view.menu.change_relation')}
                      </ListItemText>
                    </MenuItem>
                  )}
                  {hasUpdatePermission && (
                    <MenuItem
                      onClick={() => {
                        if (selectedMember) {
                          NiceModal.show(ChangeJobTitleModal, {
                            companyId,
                            userId: selectedMember.user.id,
                            workspaceModal: false
                          })
                          handleCloseMenu()
                        }
                      }}
                    >
                      <ListItemIcon>
                        <DriveFileRenameOutlineOutlinedIcon fontSize='small' />
                      </ListItemIcon>
                      <ListItemText>
                        {t('members_view.menu.change_job')}
                      </ListItemText>
                    </MenuItem>
                  )}
                  {hasDeletePermission && (
                    <MenuItem
                      onClick={() => {
                        if (selectedMember) {
                          NiceModal.show(ExpelMemberFromCompanyModal, {
                            companyId,
                            user: selectedMember,
                            hasMoreFullAccessUsers: hasMoreFullAccessUsers(
                              members,
                              selectedMember.user.id
                            )
                          })
                          handleCloseMenu()
                        }
                      }}
                    >
                      <ListItemIcon sx={{ color: 'error.main' }}>
                        <DeleteOutlineOutlinedIcon fontSize='small' />
                      </ListItemIcon>
                      <ListItemText sx={{ color: 'error.main' }}>
                        {t('members_view.menu.expel')}
                      </ListItemText>
                    </MenuItem>
                  )}
                </MenuList>
              </Menu>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  )
}
